// PWA Install Prompt — high-intent hero card.
// Shown after the user saves their first journal entry (set in JournalWrite),
// not on first load. Two surfaces:
//   1. Android / Chrome — captures `beforeinstallprompt` and shows a hero card
//      with value props and a single Install CTA.
//   2. iOS Safari — Safari does not fire `beforeinstallprompt`. Shows a
//      step-by-step guide with the Share icon highlighted in a mock toolbar.
//
// Dismiss model:
//   - "Maybe later" → sessionStorage (returns next session)
//   - "Don't show again" → localStorage (permanent)
//   - Successful install → permanent

import { useEffect, useState } from "react";
import { Bell, Zap, WifiOff, Smartphone, Share, Plus, X, Check, Download } from "lucide-react";
import { setPWAInstallModalOpen } from "@/lib/pwaInstallModal";
import { isNativeApp } from "@/lib/platform";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_DISMISS = "pwa-install-dismissed";       // permanent dismiss
const SESSION_DISMISS = "pwa-install-dismissed-session"; // 1-session snooze
const TRIGGER_FLAG    = "first-journal-entry-saved";   // set by JournalWrite

const GOLD = "#c9a84c";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return;
    if (localStorage.getItem(STORAGE_DISMISS)) return;
    if (sessionStorage.getItem(SESSION_DISMISS)) return;

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || (window.navigator as any).standalone === true;
    if (isStandalone) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Gate on the first-journal-saved milestone. iOS still needs to wait for
    // the user to be on this page after their first save.
    const triggered = () => localStorage.getItem(TRIGGER_FLAG) === "1";

    if (ios) {
      if (!triggered()) return;
      const t = setTimeout(() => setShowCard(true), 600);
      return () => clearTimeout(t);
    }

    // Android: capture the install prompt event whenever the browser fires it,
    // but only show the card once the milestone is reached.
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (triggered()) setShowCard(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // If the user already journaled before this session and the browser has
    // already cached the install eligibility, the event won't refire — poll
    // briefly so the card still appears once the flag is set during this load.
    const poll = window.setInterval(() => {
      if (triggered() && deferredPrompt) {
        setShowCard(true);
        window.clearInterval(poll);
      }
    }, 1500);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.clearInterval(poll);
    };
  }, [deferredPrompt]);

  // Broadcast modal open/close so IOSInstallBanner can step aside while the
  // hero modal is up. Always reset to false on unmount.
  useEffect(() => {
    setPWAInstallModalOpen(showCard);
    return () => setPWAInstallModalOpen(false);
  }, [showCard]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(STORAGE_DISMISS, "1");
    }
    setShowCard(false);
    setDeferredPrompt(null);
  };

  const dismissForSession = () => {
    sessionStorage.setItem(SESSION_DISMISS, "1");
    setShowCard(false);
  };

  const dismissForever = () => {
    localStorage.setItem(STORAGE_DISMISS, "1");
    setShowCard(false);
  };

  if (!showCard) return null;
  if (!isIOS && !deferredPrompt) return null; // Android needs the event

  return (
    <>
      <div className="pwa-overlay" onClick={dismissForSession} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="pwa-title" className="pwa-card">
        <button
          type="button"
          aria-label="Close"
          onClick={dismissForSession}
          className="pwa-close"
        >
          <X size={16} />
        </button>

        <div className="pwa-hero">
          <div className="pwa-icon" aria-hidden="true">TS</div>
          <div>
            <div className="pwa-eyebrow">{isIOS ? "iOS · Safari" : "The Standard"}</div>
            <h2 id="pwa-title" className="pwa-title">
              {isIOS ? <>Add to home<br />screen.</> : <>Install the<br />full app.</>}
            </h2>
          </div>
        </div>

        {isIOS ? <IOSSteps onDone={() => { localStorage.setItem(STORAGE_DISMISS, "1"); setShowCard(false); }} /> : <AndroidBenefits onInstall={handleInstall} />}

        <button type="button" onClick={dismissForever} className="pwa-secondary">
          Don't show again
        </button>

        <div className="pwa-legal">
          <span className="pwa-dot" aria-hidden="true" />
          100% private · No tracking · 0.4 MB
        </div>
      </div>
      <style>{styles}</style>
    </>
  );
}

// ── Android benefits + CTA ───────────────────────────────────────────────────

function AndroidBenefits({ onInstall }: { onInstall: () => void }) {
  return (
    <>
      <p className="pwa-sub">
        Add to your home screen for the full experience. Faster, distraction-free, built for daily commitment.
      </p>
      <ul className="pwa-benefits">
        <Benefit icon={<Zap size={14} />}      label="Faster loading"     sub="2× quicker than browser" />
        <Benefit icon={<Bell size={14} />}     label="Push notifications" sub="Daily check-in reminders" />
        <Benefit icon={<WifiOff size={14} />}  label="Works offline"      sub="Journal anywhere, sync later" />
        <Benefit icon={<Smartphone size={14} />} label="Home screen icon" sub="No tabs to find" />
      </ul>
      <button type="button" className="pwa-primary" onClick={onInstall}>
        <Download size={18} /> Install on this device
      </button>
    </>
  );
}

function Benefit({ icon, label, sub }: { icon: React.ReactNode; label: string; sub: string }) {
  return (
    <li className="pwa-benefit">
      <span className="pwa-benefit-icon">{icon}</span>
      <div>
        <div className="pwa-benefit-label">{label}</div>
        <div className="pwa-benefit-sub">{sub}</div>
      </div>
    </li>
  );
}

// ── iOS step-by-step guide ───────────────────────────────────────────────────

function IOSSteps({ onDone }: { onDone: () => void }) {
  return (
    <>
      <p className="pwa-sub">
        iOS only delivers notifications when the app is installed. Three taps gets you there.
      </p>
      <ol className="pwa-steps">
        <li className="pwa-step">
          <span className="pwa-step-num">1</span>
          <div>
            <div className="pwa-step-title">Tap the Share icon</div>
            <div className="pwa-step-desc">It's in the bottom toolbar of Safari.</div>
            <div className="pwa-safari-bar" aria-hidden="true">
              <span>◀</span>
              <span>▶</span>
              <span className="pwa-share-target"><Share size={16} /></span>
              <span>📖</span>
              <span>⊞</span>
            </div>
          </div>
        </li>
        <li className="pwa-step">
          <span className="pwa-step-num">2</span>
          <div>
            <div className="pwa-step-title">Choose "Add to Home Screen"</div>
            <div className="pwa-step-desc">Scroll if you don't see it right away.</div>
            <div className="pwa-illustration pwa-illustration-gold">
              <span className="pwa-illu-icon"><Plus size={12} /></span>
              Add to Home Screen
              <span className="pwa-arrow">→</span>
            </div>
          </div>
        </li>
        <li className="pwa-step">
          <span className="pwa-step-num">3</span>
          <div>
            <div className="pwa-step-title">Open from your home screen</div>
            <div className="pwa-step-desc">Tap <strong style={{ color: GOLD }}>TS</strong>, not Safari, so notifications work.</div>
            <div className="pwa-illustration">
              <span className="pwa-illu-icon pwa-illu-app">TS</span>
              The Standard
            </div>
          </div>
        </li>
      </ol>
      <button type="button" className="pwa-primary" onClick={onDone}>
        <Check size={18} /> I installed it - continue
      </button>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = `
.pwa-overlay {
  position: fixed; inset: 0; z-index: 90;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px);
  animation: pwaFadeIn 0.2s ease;
}
.pwa-card {
  position: fixed;
  left: 16px; right: 16px; bottom: 16px;
  z-index: 91;
  margin: 0 auto;
  max-width: 480px;
  background: linear-gradient(180deg, #1a1f2c, #131722);
  border: 1px solid rgba(201,168,76,0.35);
  border-radius: 20px;
  padding: 22px 20px 18px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(201,168,76,0.08);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  animation: pwaSlideUp 0.28s ease;
}
.pwa-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, ${GOLD}, transparent);
  border-radius: 20px 20px 0 0;
}
@keyframes pwaFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes pwaSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

.pwa-close {
  position: absolute; top: 14px; right: 14px;
  width: 32px; height: 32px; border-radius: 50%;
  background: #232938; border: 1px solid var(--hairline);
  color: #8a9ab5; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}

.pwa-hero { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
.pwa-icon {
  width: 60px; height: 60px;
  background: linear-gradient(135deg, ${GOLD}, #e8c25f);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 900; color: #0a0c12;
  font-size: 26px; letter-spacing: 1.5px;
  box-shadow: 0 6px 20px rgba(201,168,76,0.4), inset 0 -2px 0 rgba(0,0,0,0.2);
  flex-shrink: 0;
}
.pwa-eyebrow {
  font-family: 'Barlow Condensed', sans-serif;
  color: ${GOLD};
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 2px;
  margin-bottom: 2px;
}
.pwa-title {
  font-family: 'Barlow Condensed', sans-serif;
  color: #f5f5f5;
  font-size: 22px; font-weight: 800; line-height: 1.05;
  text-transform: uppercase; letter-spacing: 1px;
  margin: 0;
}
.pwa-sub { color: #8a9ab5; font-size: 13px; line-height: 1.45; margin: 0 0 14px; }

.pwa-benefits { list-style: none; margin: 0 0 16px; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pwa-benefit {
  background: rgba(255,255,255,0.025);
  border: 1px solid var(--hairline);
  border-radius: 10px;
  padding: 8px 10px;
  display: flex; align-items: center; gap: 8px;
}
.pwa-benefit-icon {
  width: 26px; height: 26px;
  border-radius: 6px;
  background: rgba(201,168,76,0.14);
  border: 1px solid rgba(201,168,76,0.30);
  color: ${GOLD};
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.pwa-benefit-label { font-size: 11px; font-weight: 600; color: #f5f5f5; line-height: 1.2; }
.pwa-benefit-sub { color: #5e6a82; font-size: 10px; margin-top: 2px; line-height: 1.2; }

.pwa-steps { list-style: none; margin: 0 0 16px; padding: 0; }
.pwa-step {
  display: flex; gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed var(--hairline);
}
.pwa-step:last-child { border-bottom: 0; }
.pwa-step-num {
  flex-shrink: 0;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: ${GOLD};
  color: #0a0c12;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 800; font-size: 13px;
  box-shadow: 0 0 0 3px rgba(201,168,76,0.18);
}
.pwa-step-title { font-weight: 700; color: #f5f5f5; font-size: 13px; margin-bottom: 4px; }
.pwa-step-desc { color: #8a9ab5; font-size: 12px; line-height: 1.45; }

.pwa-safari-bar {
  margin-top: 8px;
  padding: 8px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--hairline);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: space-around;
  color: #8a9ab5; font-size: 11px;
  position: relative;
}
.pwa-share-target { color: #0a84ff; position: relative; display: inline-flex; }
.pwa-share-target::after {
  content: '';
  position: absolute; inset: -6px;
  border: 2px solid ${GOLD};
  border-radius: 12px;
  box-shadow: 0 0 0 4px rgba(201,168,76,0.15);
  animation: pwaRing 1.6s infinite;
}
@keyframes pwaRing { 0%, 100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.08); opacity: 0.4; } }

.pwa-illustration {
  margin-top: 8px;
  background: #232938;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  padding: 6px 10px;
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; color: #f5f5f5;
}
.pwa-illustration-gold {
  background: rgba(201,168,76,0.12);
  border-color: rgba(201,168,76,0.30);
}
.pwa-illu-icon {
  width: 22px; height: 22px;
  background: rgba(0,122,255,0.15);
  border: 1px solid rgba(0,122,255,0.3);
  color: #0a84ff;
  border-radius: 5px;
  display: inline-flex; align-items: center; justify-content: center;
}
.pwa-illustration-gold .pwa-illu-icon {
  background: rgba(201,168,76,0.14);
  border-color: rgba(201,168,76,0.30);
  color: ${GOLD};
}
.pwa-illu-app {
  background: linear-gradient(135deg, ${GOLD}, #e8c25f) !important;
  color: #0a0c12 !important;
  border: 0 !important;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 900; font-size: 11px;
}
.pwa-arrow {
  color: ${GOLD};
  font-size: 14px;
  animation: pwaPulse 1.6s infinite;
}
@keyframes pwaPulse { 0%, 100% { transform: translateX(0); opacity: 0.6; } 50% { transform: translateX(4px); opacity: 1; } }

.pwa-primary {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%;
  background: linear-gradient(180deg, #e8c25f, ${GOLD});
  color: #0a0c12;
  border: 0; border-radius: 12px;
  padding: 14px 20px;
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 800;
  font-size: 14px; letter-spacing: 2px;
  text-transform: uppercase;
  box-shadow: 0 6px 20px rgba(201,168,76,0.35), inset 0 -2px 0 rgba(0,0,0,0.2);
  cursor: pointer;
}
.pwa-primary:active { transform: translateY(1px); }

.pwa-secondary {
  background: transparent; color: #8a9ab5;
  border: 0;
  display: block; margin: 10px auto 0;
  padding: 6px;
  font-size: 12px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.pwa-legal {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 10px;
  color: #5e6a82; font-size: 10.5px;
  font-family: 'Roboto Mono', monospace;
  letter-spacing: 1px; text-transform: uppercase;
}
.pwa-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #22c55e; box-shadow: 0 0 8px #22c55e;
}
`;
